import { api, type StockCheckDetail, type StoresOverview } from '../api';
import { callout, field, input, opt, openDialog, select, val, numVal } from '../dialog';
import { TAG_STYLE, expiryText } from '../stock-ui';
import { errorCard, esc, fmtDate, html, loading, pill, tile, toast } from '../ui';

/**
 * The stock check.
 *
 * Counted, reconciled, signed. The expected figure on each line was frozen
 * when the check opened, so a movement made during the count cannot quietly
 * reconcile a discrepancy away — which is the one thing a stock check exists
 * to surface.
 *
 * Signing is what applies the corrections. Until then a count is an
 * observation, and every adjustment it produces writes a movement naming who
 * made it, so the figure on the shelf never changes without a row explaining
 * why.
 *
 * Any member of the organisation may sign. No regulation names who counts
 * stock, and inventing an authorisation for it would mean a one-person shop
 * could never close a check.
 */

function view(c: StockCheckDetail): string {
  const outstanding = c.lines.filter((l) => l.countedQuantity === null);
  const done = c.state !== 'OPEN';

  return html`
    <div class="page-h">
      <div>
        <p class="tiny dim"><a href="/stores">← Stores</a></p>
        <h1 style="font-family:var(--mono)">${esc(c.reference)}</h1>
        <p class="dim">
          ${esc(c.title)} · started ${esc(fmtDate(c.startedAt))}
          ${c.signedAt ? ` · signed ${esc(fmtDate(c.signedAt.slice(0, 10)))} by ${esc(c.signedBy ?? '')}` : ''}
        </p>
      </div>
      <div style="display:flex;gap:8px">
        ${done ? '' : '<button class="btn" data-abandon>Abandon</button>'}
        ${done ? '' : '<button class="btn pri" data-sign>Sign it off</button>'}
      </div>
    </div>

    <div class="tiles">
      ${tile({ label: 'Counted', value: `${c.progress.counted} / ${c.progress.total}`, sub: outstanding.length ? `${outstanding.length} to go` : 'all counted', tone: outstanding.length ? 'caution' : 'ok' })}
      ${tile({ label: 'Discrepancies', value: c.progress.discrepancies, sub: 'count differs from the system', tone: c.progress.discrepancies ? 'caution' : 'ok' })}
      ${tile({ label: 'In the wrong place', value: c.progress.misfiled, sub: 'not where the system said', tone: c.progress.misfiled ? 'caution' : 'ok' })}
      ${tile({ label: 'Label missing', value: c.progress.unlabelled, sub: 'no legible identification', tone: c.progress.unlabelled ? 'caution' : 'ok' })}
    </div>

    ${done
      ? callout(c.state === 'SIGNED' ? 'ok' : 'info', c.state === 'SIGNED' ? '✓' : 'i', html`
          This check is ${esc(c.state.toLowerCase())} and can no longer be edited.
          ${c.note ? html`<br>${esc(c.note)}` : ''}
        `)
      : callout('info', 'i', html`
          Count each lot and record what was actually on the shelf. The expected figure was frozen when
          this check opened, so anything issued since shows as a discrepancy rather than being absorbed.
          Signing applies the corrections and writes a movement for each one.
        `)}

    <div class="card">
      <div class="card-h">
        <div>
          <h2>Lots to count</h2>
          <p class="tiny dim">${c.lines.length} on this check</p>
        </div>
      </div>
      <div class="tblwrap">
        <table class="tbl">
          <thead>
            <tr>
              <th>Part number</th><th class="wide">Description</th><th class="col-opt">Location</th>
              <th>Condition</th><th>Expected</th><th>Counted</th><th></th>
            </tr>
          </thead>
          <tbody>
            ${c.lines
              .map((l) => {
                const counted = l.countedQuantity !== null;
                const off = counted && l.variance !== 0;
                return html`
                  <tr>
                    <td class="st s-${off ? 'caution' : counted ? 'ok' : 'na'} n">${esc(l.lot.partNumber)}</td>
                    <td class="wide">
                      ${esc(l.lot.description)}
                      ${l.lot.batchLot ? `<span class="sub2">batch ${esc(l.lot.batchLot)}</span>` : ''}
                      ${l.note ? `<span class="sub2">${esc(l.note)}</span>` : ''}
                    </td>
                    <td class="col-opt tiny muted">
                      ${l.lot.location ? esc(l.lot.location.code) : '—'}
                      ${l.locationOk === false ? '<span class="sub2" style="color:var(--caution)">not there</span>' : ''}
                    </td>
                    <td style="white-space:nowrap">
                      ${l.lot.tag
                        ? `<span class="chip" style="${TAG_STYLE[l.lot.tag]}">${esc(l.lot.stateLabel)}</span>`
                        : `<span class="chip">${esc(l.lot.stateLabel)}</span>`}
                      ${l.lot.expired ? `<span class="sub2" style="color:var(--exceed)">${esc(expiryText(l.lot))}</span>` : ''}
                    </td>
                    <td class="n">${esc(String(l.expectedQuantity))} ${esc(l.lot.unit)}</td>
                    <td class="n">
                      ${counted
                        ? html`${esc(String(l.countedQuantity))} ${esc(l.lot.unit)}
                            ${off ? pill('DUE_SOON', `${l.variance! > 0 ? '+' : ''}${l.variance}`) : ''}`
                        : '<span class="tiny muted">not counted</span>'}
                    </td>
                    <td style="white-space:nowrap">
                      ${done ? '' : `<button class="btn sm" data-count="${esc(l.id)}">${counted ? 'Recount' : 'Count'}</button>`}
                    </td>
                  </tr>
                `;
              })
              .join('')}
          </tbody>
        </table>
      </div>
      <div class="card-f">
        A check cannot be signed with lines uncounted: a signature on a partial count says the shelf was
        verified when it was not.
      </div>
    </div>
  `;
}

// ── dialogs ─────────────────────────────────────────────────────────────────

function countLine(
  check: StockCheckDetail,
  line: StockCheckDetail['lines'][number],
  people: StoresOverview['people'],
  reload: () => void,
): void {
  openDialog<Record<string, unknown>>({
    title: 'Count it',
    sub: `${line.lot.partNumber} — ${line.lot.description.slice(0, 60)}`,
    primary: 'Record the count',
    body: () => html`
      <div class="frow">
        ${field('How many are there', input('countedQuantity', String(line.countedQuantity ?? line.expectedQuantity),
          'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'),
          `The system says ${line.expectedQuantity} ${line.lot.unit}`)}
        ${field('Counted by', select('countedByPersonId', [
          opt('', 'Choose…'),
          ...people.map((p) => opt(p.id, p.displayName, p.id === line.countedBy)),
        ].join('')))}
      </div>
      <div class="frow">
        ${field('Was it where the system said', select('locationOk', [
          opt('true', `Yes — ${line.lot.location?.code ?? 'as recorded'}`, line.locationOk !== false),
          opt('false', 'No — found somewhere else', line.locationOk === false),
        ].join('')))}
        ${field('Label and tag legible', select('labelOk', [
          opt('true', 'Yes', line.labelOk !== false),
          opt('false', 'No — needs reprinting', line.labelOk === false),
        ].join('')))}
      </div>
      ${field('Note', input('note', line.note ?? '', 'placeholder="Two litres unaccounted for" maxlength="1000"'))}
    `,
    collect: (root) => {
      const countedQuantity = numVal(root, 'countedQuantity');
      if (countedQuantity === undefined || countedQuantity < 0) {
        return { error: 'Record what was actually there, even if it is zero.' };
      }
      return {
        countedQuantity,
        locationOk: val(root, 'locationOk') === 'true',
        labelOk: val(root, 'labelOk') === 'true',
        note: val(root, 'note') || undefined,
        countedByPersonId: val(root, 'countedByPersonId') || undefined,
      };
    },
    submit: (body) => api.stores.countLine(check.id, line.id, body),
    done: () => 'Counted',
    onDone: reload,
  });
}

function signCheck(check: StockCheckDetail, people: StoresOverview['people'], reload: () => void): void {
  const adjustments = check.lines.filter((l) => l.variance !== null && l.variance !== 0);

  openDialog<Record<string, unknown>>({
    title: `Sign off ${check.reference}`,
    sub: 'Applies the corrections and closes the check',
    primary: 'Sign it',
    body: () => html`
      ${field('Signed by', select('signedByPersonId', [
        opt('', 'Choose…'),
        ...people.map((p) => opt(p.id, p.jobTitle ? `${p.displayName} — ${p.jobTitle}` : p.displayName)),
      ].join('')))}
      ${field('Note', input('note', '', 'placeholder="Anything the next reader needs" maxlength="4000"'))}
      ${adjustments.length
        ? callout('caution', '!', html`
            <b>${adjustments.length} lot(s) will be corrected to the counted figure.</b>
            <ul style="margin:6px 0 0 18px">
              ${adjustments.slice(0, 8).map((l) => html`
                <li>${esc(l.lot.partNumber)} — ${l.expectedQuantity} → ${l.countedQuantity} ${esc(l.lot.unit)}</li>
              `).join('')}
              ${adjustments.length > 8 ? `<li>…and ${adjustments.length - 8} more</li>` : ''}
            </ul>
            Each one writes a movement naming you, so the change is never anonymous.
          `)
        : callout('ok', '✓', 'Every lot counted as expected — nothing will be adjusted.')}
    `,
    collect: (root) => {
      const signedByPersonId = val(root, 'signedByPersonId');
      if (!signedByPersonId) return { error: 'Who is signing it off?' };
      return { signedByPersonId, note: val(root, 'note') || undefined };
    },
    submit: (body) => api.stores.signCheck(check.id, body),
    done: (r) => `${r.reference} signed off`,
    onDone: reload,
  });
}

function abandonCheck(check: StockCheckDetail, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    title: `Abandon ${check.reference}`,
    sub: 'The counts already recorded are kept, but nothing is corrected',
    primary: 'Abandon it',
    body: () => field('Why', input('reason', '', 'placeholder="Superseded by a full recount after the move" maxlength="400"')),
    collect: (root) => {
      const reason = val(root, 'reason');
      if (!reason) return { error: 'Say why the check was abandoned.' };
      return { reason };
    },
    submit: (body) => api.stores.abandonCheck(check.id, body),
    done: () => 'Check abandoned',
    onDone: reload,
  });
}

/** Starting one, from the stores screen. */
export function openStockCheck(onDone: (id: string) => void): void {
  openDialog<Record<string, unknown>>({
    title: 'Start a stock check',
    sub: 'Every lot on the shelf, frozen at what the system believes it holds',
    primary: 'Start it',
    body: () => html`
      ${field('What to call it', input('title', `Stock check ${new Date().getFullYear()}`, 'maxlength="200"'))}
      ${field('Started on', input('startedAt', new Date().toISOString().slice(0, 10), 'type="date"'))}
      ${callout('info', 'i', html`
        Each lot's expected figure is written onto the check now and does not move afterwards. That is
        what makes a discrepancy visible: if the check read the live figure, anything issued during the
        count would silently agree with itself.
      `)}
    `,
    collect: (root) => ({ title: val(root, 'title'), startedAt: val(root, 'startedAt') }),
    submit: (body) => api.stores.openCheck(body),
    done: (r) => `${r.reference} opened — ${r.lines.length} lots to count`,
    onDone: (r: StockCheckDetail) => onDone(r.id),
  });
}

// ── entry point ─────────────────────────────────────────────────────────────

export async function renderStockCheck(host: HTMLElement, id: string): Promise<void> {
  host.innerHTML = loading('Loading the stock check');
  try {
    const [check, overview] = await Promise.all([api.stores.check(id), api.stores.overview()]);
    host.innerHTML = view(check);
    const reload = () => void renderStockCheck(host, id);

    host.querySelector('[data-sign]')?.addEventListener('click', () => {
      if (check.progress.counted < check.progress.total) {
        return toast(
          `${check.progress.total - check.progress.counted} lot(s) still to count.`,
          'error',
        );
      }
      signCheck(check, overview.people, reload);
    });
    host.querySelector('[data-abandon]')?.addEventListener('click', () => abandonCheck(check, reload));
    host.querySelectorAll<HTMLElement>('[data-count]').forEach((b) =>
      b.addEventListener('click', () => {
        const line = check.lines.find((l) => l.id === b.dataset.count);
        if (line) countLine(check, line, overview.people, reload);
      }),
    );
  } catch (err) {
    host.innerHTML = errorCard(err instanceof Error ? err.message : 'Could not load the stock check.');
  }
}
